import { InlineAlert } from '@xo-union/tk-component-alerts';
import { Button } from '@xo-union/tk-component-buttons';
import Modal, { ModalHeader } from '@xo-union/tk-component-modals';
import { Form, Formik } from 'formik';
import Honeybadger from 'honeybadger-js';
import React, { useMemo } from 'react';
import * as Yup from 'yup';
import { FormColumn, FormRow } from '@xo-union/tk-component-form-grid';
import { useUpdateWeddingInfoMutation, useWeddingInfoQuery, WeddingInfoQuery } from '../../generated/graphql';
import WeddingInfoFormBody from './form/WeddingInfoFormBody';
import styles from './messages.scss';
import moment from 'moment';
import { Sequence } from './messagingHubHelper';

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  fianceFirstName: Yup.string().required('Partner First Name is required'),
  fianceLastName: Yup.string().required('Partner Last Name is required'),
  weddingDate: Yup.date().required('Wedding date is required'),
  weddingLocation: Yup.string(),
});

interface Props {
  // Can be either HubSequence or String depending on whether the parent component is the Rail or the Hub
  onSubmit: (sequenceId?: Sequence | string) => void;
  onClose: () => void;
  sequence?: Sequence | string;
  initialData?: {
    firstName?: string;
    lastName?: string;
    fianceFirstName?: string;
    fianceLastName?: string;
    weddingDate?: string;
    weddingLocation?: string;
  };
}

export interface WeddingInfoProps {
  firstName: string;
  lastName: string;
  fianceFirstName: string;
  fianceLastName: string;
  weddingDate: string;
  weddingLocation: string;
}

function getInitialValues(data?: WeddingInfoQuery, initialData?: any) {
  return {
    firstName: data?.member?.firstName || initialData?.firstName || '',
    lastName: data?.member?.lastName || initialData?.lastName || '',
    fianceFirstName: data?.member?.fianceFirstName || initialData?.fianceFirstName || '',
    fianceLastName: data?.member?.fianceLastName || initialData?.fianceLastName || '',
    weddingDate: data?.wedding?.weddingDate || initialData?.weddingDate || moment().format('YYYY-MM-DD'),
    weddingLocation: data?.weddingWebsite?.weddingLocation || initialData?.weddingLocation || '',
  };
}
const WeddingInfoModal: React.FC<Props> = ({ onSubmit, onClose, sequence, initialData = {} }) => {
  const [updateWeddingInfo, { error }] = useUpdateWeddingInfoMutation();
  const { data } = useWeddingInfoQuery();
  const initialValues = useMemo(() => getInitialValues(data, initialData), [data, initialData]);

  const handleSubmit = async (values: WeddingInfoProps) => {
    const { weddingDate, weddingLocation, ...memberArgs } = values;
    try {
      await updateWeddingInfo({
        variables: {
          membersArgs: memberArgs,
          weddingDateArgs: {
            wedding: {
              weddingDate,
            },
          },
          weddingWebsiteAttrs: {
            weddingLocation,
          },
        },
      });

      if (sequence) {
        onSubmit(sequence);
      } else {
        onSubmit();
      }
    } catch (error) {
      Honeybadger.notify('Error required Save The Date fields', {
        context: {
          error,
        },
      });
    }
  };

  return (
    <Modal
      size="sm"
      onCloseButtonClick={onClose}
      onOverlayClick={() => {
        /* don't close modal by clicking outside it */
      }}
      className={styles.modalContainer}
    >
      {error && <InlineAlert type="error">Something went wrong</InlineAlert>}
      <ModalHeader>Please add your names and wedding date to continue</ModalHeader>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
        {({ submitForm, isSubmitting }) => (
          <Form>
            <WeddingInfoFormBody />
            <FormRow className={styles.buttonRow}>
              <FormColumn md="6">
                <Button
                  color="secondary"
                  onClick={onClose}
                  data-testid="cancel-info-button"
                  className={styles.nameButton}
                  size="lg"
                  block
                >
                  Cancel
                </Button>
              </FormColumn>
              <FormColumn md="6">
                <Button
                  disabled={isSubmitting}
                  data-testid="save-info-button"
                  className={styles.nameButton}
                  onClick={submitForm}
                  size="lg"
                  type="submit"
                  block
                >
                  Save
                </Button>
              </FormColumn>
            </FormRow>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default WeddingInfoModal;
